const paymentAmount = value => Math.max(0, Number(value || 0));
const roundMoney = value => Math.round((Number(value) || 0) * 100) / 100;

const normalizeInstallment = (installment, paidAmount = installment?.odeme_tutari, paymentInfo = {}) => {
  const amount = paymentAmount(installment?.tutar);
  const paid = Math.min(amount, paymentAmount(paidAmount));
  const remaining = Math.max(0, amount - paid);
  let status = installment?.durum;

  if (status !== 'iptal') {
    if (remaining <= 0) status = 'odendi';
    else if (paid > 0) status = 'kismi';
    else if (status === 'odendi' || status === 'kismi') status = 'bekliyor';
  }

  return {
    ...installment,
    ...paymentInfo,
    odeme_tutari: paid,
    kalan_tutar: remaining,
    durum: status,
  };
};

export const reconcileInstallmentPayments = installments => {
  return (installments || []).map(installment => normalizeInstallment(installment));
};

export const summarizePaymentsByContract = installments => {
  const summaries = new Map();
  reconcileInstallmentPayments(installments).forEach(installment => {
    if (installment.durum === 'iptal') return;
    const key = String(installment.sozlesme_id);
    const summary = summaries.get(key) || { odenen_tutar: 0, kalan_tutar: 0 };
    summary.odenen_tutar += paymentAmount(installment.odeme_tutari);
    summary.kalan_tutar += paymentAmount(installment.kalan_tutar);
    summaries.set(key, summary);
  });
  return summaries;
};

export const buildSelectedInstallmentPaymentPreview = (
  installments,
  value,
  contractId = null,
  selectedInstallmentId = null
) => {
  const amount = roundMoney(value);
  const orderedInstallments = (installments || [])
    .filter(installment => (
      installment.durum !== 'iptal'
      && (contractId === null || contractId === undefined
        || String(installment.sozlesme_id) === String(contractId))
    ))
    .map(installment => ({
      ...installment,
      odeme_tutari: paymentAmount(installment.odeme_tutari),
      kalan_tutar: Math.max(
        0,
        roundMoney(
          installment.kalan_tutar
          ?? (paymentAmount(installment.tutar) - paymentAmount(installment.odeme_tutari))
        )
      ),
    }))
    .sort((a, b) => (
      String(a.vade_tarihi || '').localeCompare(String(b.vade_tarihi || ''))
      || Number(a.taksit_no || a.id) - Number(b.taksit_no || b.id)
    ));

  const openInstallments = orderedInstallments.filter(installment => installment.kalan_tutar > 0);
  const selectedIndex = orderedInstallments.findIndex(
    installment => String(installment.id) === String(selectedInstallmentId)
  );

  const allocationOrder = selectedIndex < 0
    ? openInstallments
    : [
      // Seçilen taksitten önce yarım kalmış ödeme varsa önce onu tamamla.
      ...orderedInstallments.slice(0, selectedIndex).filter(
        installment => installment.kalan_tutar > 0 && installment.odeme_tutari > 0
      ),
      // Ardından tıklanan taksiti ve varsa sonraki taksitleri sırayla öde.
      ...orderedInstallments.slice(selectedIndex).filter(installment => installment.kalan_tutar > 0),
      // Çok yüksek bir tahsilat girilirse daha eski, hiç ödeme almamış satırları en son değerlendir.
      ...orderedInstallments.slice(0, selectedIndex).filter(
        installment => installment.kalan_tutar > 0 && installment.odeme_tutari <= 0
      ),
    ];

  const totalRemaining = roundMoney(openInstallments.reduce(
    (sum, installment) => sum + installment.kalan_tutar,
    0
  ));

  if (!Number.isFinite(Number(value)) || amount <= 0) {
    return { allocations: [], extra: 0, totalRemaining, afterRemaining: totalRemaining };
  }

  let available = amount;
  const allocations = [];
  allocationOrder.forEach(installment => {
    if (available <= 0) return;
    const applied = Math.min(available, installment.kalan_tutar);
    allocations.push({
      ...installment,
      applied: roundMoney(applied),
      after: roundMoney(installment.kalan_tutar - applied),
    });
    available = roundMoney(available - applied);
  });

  return {
    allocations,
    extra: Math.max(0, available),
    totalRemaining,
    afterRemaining: Math.max(0, roundMoney(totalRemaining - amount)),
  };
};

// Geçiş döneminde eski import adını kullanan ekranlar için geriye dönük uyumluluk.
export const buildOldestFirstPaymentPreview = buildSelectedInstallmentPaymentPreview;

export const resolvePaymentPostingInstallmentId = (preview, fallbackId = null) => (
  preview?.allocations?.[0]?.id ?? fallbackId
);
